import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { Tooltip } from 'antd';
import { FullscreenOutlined, CopyOutlined, FileImageOutlined, CheckOutlined } from '@ant-design/icons';
import * as THREE from 'three';
import { useAppStore } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';

//  Constants
const CHECKER_SIZE = 16; // CSS pixels per checker square


//  InvalidateCapture
// Lives inside the R3F <Canvas> and exports the `invalidate` function so
// the parent component can schedule demand-mode redraws from outside.
function InvalidateCapture({ target }: { target: React.MutableRefObject<(() => void) | null> }) {
  const { invalidate } = useThree();
  target.current = invalidate;
  return null;
}

//  UVScene
// The Three.js scene rendered inside the UV panel Canvas.
// Three full-screen planes at different Z depths:
//   z = -0.3  background (checker or solid color)
//   z = -0.2  committed texture  (CanvasTexture from textureCanvas / uvFullCanvas)
//   z = -0.1  live stroke overlay (CanvasTexture from strokeLiveCanvas)
interface UVSceneProps {
  baseTexRef:   React.MutableRefObject<THREE.CanvasTexture | null>;
  strokeTex:    THREE.CanvasTexture;
  checkerTex:   THREE.CanvasTexture;
  toolMode:     string;
  brushOpacity: number;
  bgMode:       string;
}

function UVScene({ baseTexRef, strokeTex, checkerTex, toolMode, brushOpacity, bgMode }: UVSceneProps) {
  const { size } = useThree();
  const w = size.width;
  const h = size.height;

  // Base CanvasTexture re-created only when the source canvas reference changes
  const sourceCanvas = useAppStore(s => s.uvFullCanvas ?? s.textureCanvas);
  const baseTex = useMemo(() => {
    if (!sourceCanvas) return null;
    const t = new THREE.CanvasTexture(sourceCanvas);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }, [sourceCanvas]);

  useEffect(() => {
    baseTexRef.current = baseTex;
  }, [baseTex, baseTexRef]);


  useEffect(() => {
    checkerTex.repeat.set(w / (CHECKER_SIZE * 2), h / (CHECKER_SIZE * 2));
    checkerTex.needsUpdate = true;
  }, [w, h, checkerTex]);

  //  Stroke overlay opacity  
  const strokeOpacity =
    toolMode === 'erase'                            ? brushOpacity * 0.5
    : toolMode === 'stamp' || toolMode === 'text'   ? 0.6
    : brushOpacity;

  return (
    <>
      {/* Background */}
      <mesh renderOrder={0} position={[0, 0, -0.3]}>
        <planeGeometry args={[w, h]} />
        {bgMode === 'transparent'
          ? <meshBasicMaterial map={checkerTex} />
          : <meshBasicMaterial color={bgMode === 'black' ? '#000' : '#fff'} />
        }
      </mesh>

      {/* Committed texture */}
      {baseTex && (
        <mesh renderOrder={1} position={[0, 0, -0.2]}>
          <planeGeometry args={[w, h]} />
          <meshBasicMaterial map={baseTex} transparent />
        </mesh>
      )}

      {/* Live stroke / stamp preview overlay — strokeTex is managed in UVPanel. */}
      <mesh renderOrder={2} position={[0, 0, -0.1]}>
        <planeGeometry args={[w, h]} />
        <meshBasicMaterial
          map={strokeTex}
          transparent
          opacity={strokeOpacity}
          depthWrite={false}
        />
      </mesh>
    </>
  );
}

//  UVPanel
const UVPanel = () => {
  const brushCursorRef      = useRef<HTMLDivElement>(null);
  const blurInnerCursorRef  = useRef<HTMLDivElement>(null);
  const dupCursorRef        = useRef<HTMLDivElement>(null);
  const isDrawingRef   = useRef(false);
  const uvDragModeRef  = useRef<'rotate' | 'translate' | null>(null);

  //  Store subscriptions                          
  const uvViewportMain    = useAppStore(s => s.uvViewportMain);
  const setUvViewportMain = useAppStore(s => s.setUvViewportMain);
  const toolMode      = useAppStore(s => s.toolMode);
  const brushSize     = useAppStore(s => s.brushSize);
  const brushOpacity  = useAppStore(s => s.brushOpacity);
  const blurRadius    = useAppStore(s => s.blurRadius);
  const dupMode       = useAppStore(s => s.dupMode);
  const layers        = useAppStore(s => s.layers);
  const selectedLayerId  = useAppStore(s => s.selectedLayerId);
  const uvCanvasVersion  = useAppStore(s => s.uvCanvasVersion);
  const textureVersion   = useAppStore(s => s.textureVersion);
  const texRes           = useAppStore(s => s.texRes);
  const bgMode           = useAppStore(s => s.viewportBgMode);
  const currentFrame     = useAppStore(s => s.currentFrame);
  const clipboardPasteCanvas   = useAppStore(s => s.clipboardPasteCanvas);
  const clipboardPasteScale    = useAppStore(s => s.clipboardPasteScale);
  const clipboardPasteFitScale = useAppStore(s => s.clipboardPasteFitScale);

  const isUvDrawable = uvViewportMain &&
    (toolMode === 'draw' || toolMode === 'erase' || toolMode === 'blur' ||
     toolMode === 'stamp' || toolMode === 'text');
  const isUvBrush = uvViewportMain &&
    (toolMode === 'draw' || toolMode === 'erase' || toolMode === 'blur');

  //  Imperative texture / invalidation refs
  const baseTexRef    = useRef<THREE.CanvasTexture | null>(null);
  const invalidateRef  = useRef<(() => void) | null>(null);

  // strokeTex is created here (outside R3F) and bound declaratively via map={strokeTex}.
  // IMPORTANT: Three.js uses texStorage2D (immutable GPU allocation) for CanvasTextures.
  // If the initial canvas is 1×1, texStorage2D locks the GPU texture to 1×1 and subsequent
  // texSubImage2D calls with larger data silently fail — nothing renders.
  // Fix: initialise the canvas at the actual texture resolution and never swap .image.
  // Instead, draw into the canvas with ctx.drawImage (needsUpdate triggers re-upload).
  const [strokeTex, setStrokeTex] = useState<THREE.CanvasTexture>(() => {
    const { w, h } = useAppStore.getState().texRes;
    const c = document.createElement('canvas');
    c.width = w || 2048;
    c.height = h || 2048;
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  });

  // When the project texture resolution changes, recreate the stroke canvas + texture
  // so texStorage2D gets a fresh immutable allocation at the new size.
  useEffect(() => {
    const { w, h } = texRes;
    if (!w || !h) return;
    const existing = strokeTex.image as HTMLCanvasElement;
    if (existing.width === w && existing.height === h) return;
    strokeTex.dispose();
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    setStrokeTex(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texRes]);

  //  Checker texture (created once, tiled in UVScene)            
  const checkerTex = useMemo(() => {
    const c = document.createElement('canvas');
    c.width = CHECKER_SIZE * 2; c.height = CHECKER_SIZE * 2;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#888'; ctx.fillRect(0, 0, CHECKER_SIZE * 2, CHECKER_SIZE * 2);
    ctx.fillStyle = '#bbb'; ctx.fillRect(0, 0, CHECKER_SIZE, CHECKER_SIZE);
    ctx.fillStyle = '#bbb'; ctx.fillRect(CHECKER_SIZE, CHECKER_SIZE, CHECKER_SIZE, CHECKER_SIZE);
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    return tex;
  }, []);

  //  Base texture refresh
  useEffect(() => {
    if (baseTexRef.current) {
      baseTexRef.current.needsUpdate = true;
      invalidateRef.current?.();
    }
  }, [uvCanvasVersion, textureVersion]);

  //  Stroke texture helpers
  const notifyStroke = useCallback(() => {
    const sc = viewportActions.strokeLiveCanvas.current;
    const canvas = strokeTex.image as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (sc) ctx.drawImage(sc, 0, 0);
    strokeTex.needsUpdate = true;
    invalidateRef.current?.();
  }, [strokeTex]);

  const notifyStrokeClear = useCallback(() => {
    const canvas = strokeTex.image as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokeTex.needsUpdate = true;
    invalidateRef.current?.();
  }, [strokeTex]);

  // Register as Viewport3D's rAF completion callback.
  useEffect(() => {
    viewportActions.notifyUvStrokeLive.current = notifyStroke;
    return () => { viewportActions.notifyUvStrokeLive.current = () => {}; };
  }, [notifyStroke]);

  //  Brush cursor helpers
  const updateCursor = useCallback((e: React.PointerEvent) => {
    const cursor = brushCursorRef.current;
    if (!cursor || !isUvDrawable || !isUvBrush) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const r = Math.max(4, (brushSize / (2 * Math.PI)) * rect.width);
    const borderColor =
      toolMode === 'erase' ? 'rgba(255,80,80,0.85)' :
      toolMode === 'blur'  ? 'rgba(34,211,238,0.85)' :
      'rgba(255,255,255,0.85)';
    cursor.style.border  = `1.5px solid ${borderColor}`;
    cursor.style.left    = `${x}px`;
    cursor.style.top     = `${y}px`;
    cursor.style.width   = `${r * 2}px`;
    cursor.style.height  = `${r * 2}px`;
    cursor.style.display = 'block';
    // Inner blur-radius ring — visible only in blur mode
    const inner = blurInnerCursorRef.current;
    if (inner) {
      if (toolMode === 'blur') {
        const ir = Math.max(2, blurRadius * r);
        inner.style.left    = `${x}px`;
        inner.style.top     = `${y}px`;
        inner.style.width   = `${ir * 2}px`;
        inner.style.height  = `${ir * 2}px`;
        inner.style.display = 'block';
      } else {
        inner.style.display = 'none';
      }
    }
    const dots = dupCursorRef.current;
    if (dots) {
      dots.style.left    = `${x}px`;
      dots.style.top     = `${y - r - 8}px`;
      dots.style.display = 'flex';
    }
  }, [isUvDrawable, isUvBrush, brushSize, blurRadius, toolMode]);

  const hideCursor = useCallback(() => {
    if (brushCursorRef.current)     brushCursorRef.current.style.display     = 'none';
    if (blurInnerCursorRef.current) blurInnerCursorRef.current.style.display = 'none';
    if (dupCursorRef.current)       dupCursorRef.current.style.display       = 'none';
  }, []);

  //  Pointer handlers
  const handleCanvasPointerDown = useCallback((e: React.PointerEvent) => {
    if (!isUvDrawable || e.button !== 0) return;
    e.preventDefault();

    if (e.shiftKey && e.altKey) {
      uvDragModeRef.current = 'translate';
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      viewportActions.uvTranslateStart.current(e.clientX, e.clientY);
      return;
    }
    if (e.shiftKey) {
      uvDragModeRef.current = 'rotate';
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      viewportActions.uvRotateStart.current(e.clientX, e.clientY);
      return;
    }
    uvDragModeRef.current = null;

    const rect = e.currentTarget.getBoundingClientRect();
    const u = (e.clientX - rect.left) / rect.width;
    const v = (e.clientY - rect.top)  / rect.height;

    if (toolMode === 'stamp') { viewportActions.uvStampApply.current(u, v); return; }
    if (toolMode === 'text')  { viewportActions.uvTextApply.current(u, v);  return; }

    isDrawingRef.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    updateCursor(e);
    viewportActions.uvPaintStart.current(u, v, e.pressure > 0 ? e.pressure : 1.0);
  }, [isUvDrawable, toolMode, updateCursor]);

  const handleCanvasPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isUvDrawable) return;

    if (uvDragModeRef.current === 'rotate') {
      viewportActions.uvRotateMove.current(e.clientX, e.clientY);
      return;
    }
    if (uvDragModeRef.current === 'translate') {
      const rect = e.currentTarget.getBoundingClientRect();
      viewportActions.uvTranslateMove.current(e.clientX, e.clientY, rect.width);
      return;
    }

    updateCursor(e);
    const rect = e.currentTarget.getBoundingClientRect();
    const u = (e.clientX - rect.left) / rect.width;
    const v = (e.clientY - rect.top)  / rect.height;

    if (toolMode === 'stamp') {
      viewportActions.uvStampPreviewMove.current(u, v);
      notifyStroke();
      return;
    }
    if (toolMode === 'text') {
      viewportActions.uvTextPreviewMove.current(u, v);
      notifyStroke();
      return;
    }
    if (!isUvBrush || (e.buttons & 1) === 0) return;
    viewportActions.uvPaintMove.current(u, v, e.pressure > 0 ? e.pressure : 1.0);
    // Stroke canvas is updated asynchronously (rAF in Viewport3D); notifyUvStrokeLive fires then.
  }, [isUvDrawable, toolMode, isUvBrush, updateCursor, notifyStroke]);

  const handleCanvasPointerUp = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    if (uvDragModeRef.current === 'rotate') {
      uvDragModeRef.current = null;
      viewportActions.uvRotateEnd.current();
      return;
    }
    if (uvDragModeRef.current === 'translate') {
      uvDragModeRef.current = null;
      viewportActions.uvTranslateEnd.current();
      return;
    }
    if (!isUvBrush) return;
    isDrawingRef.current = false;
    hideCursor();
    viewportActions.uvPaintEnd.current();
    notifyStrokeClear();
  }, [isUvBrush, hideCursor, notifyStrokeClear]);

  const handleCanvasPointerLeave = useCallback(() => {
    hideCursor();
    // uvClearStampPreview calls notifyUvStrokeLive internally.
    viewportActions.uvClearStampPreview.current();
  }, [hideCursor]);

  //  Save / copy read from the committed texture canvas directly
  const handleSaveFrame = useCallback(() => {
    const canvas = useAppStore.getState().uvFullCanvas ?? useAppStore.getState().textureCanvas;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `frame_${String(currentFrame + 1001).padStart(4, '0')}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }, [currentFrame]);

  const [copiedSuccess, setCopiedSuccess] = useState(false);
  const handleCopyFrame = useCallback(() => {
    const canvas = useAppStore.getState().uvFullCanvas ?? useAppStore.getState().textureCanvas;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      try {
        navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]).then(() => {
          setCopiedSuccess(true);
          setTimeout(() => setCopiedSuccess(false), 1500);
        });
      } catch { /* clipboard API unavailable */ }
    }, 'image/png');
  }, []);

  //  Clipboard paste overlay (CSS %)                    
  const pasteOverlay = useMemo(() => {
    if (!clipboardPasteCanvas || !clipboardPasteFitScale || !texRes.w || !texRes.h) return null;
    const totalScale = clipboardPasteFitScale * clipboardPasteScale;
    const fracW = (clipboardPasteCanvas.width  * totalScale) / texRes.w;
    const fracH = (clipboardPasteCanvas.height * totalScale) / texRes.h;
    return { fracW, fracH };
  }, [clipboardPasteCanvas, clipboardPasteFitScale, clipboardPasteScale, texRes]);

  const pasteImgSrc = useMemo(
    () => clipboardPasteCanvas?.toDataURL() ?? '',
    [clipboardPasteCanvas],
  );

  //  Dup-mode dot info
  const selLayer     = layers.find(l => l.id === selectedLayerId);
  const layerDupMode = selLayer?.lastDupMode;
  const dotColor =
    !layerDupMode            ? 'rgba(255,255,255,0.82)' :
    layerDupMode === dupMode ? 'rgba(80,220,80,0.9)' :
    'rgba(255,80,80,0.9)';
  const dotCount = dupMode === 'hero' ? 1 : dupMode === 'diptych' ? 2 : dupMode === 'triptych' ? 3 : 4;

  const btnBase: React.CSSProperties = {
    padding: '2px 5px', fontSize: 10, lineHeight: '16px',
    borderRadius: 3, border: '1px solid #444', cursor: 'pointer',
    background: '#1a1a2e', color: '#888', userSelect: 'none',
  };

  return (
    <div
      style={{
        height: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        border: '1px solid rgba(255,255,255,0.12)',
        background: '#0a0a14',
        overflow: 'hidden',
      }}
    >
      {/*
        Three.js Canvas replaces the old 2D <canvas>.

        R3F orthographic camera with zoom=1: 1 world unit = 1 CSS pixel.
        Each UVScene plane is sized to [size.width Ã— size.height] world units,
        which fills the viewport exactly (same stretch-to-fill as the old 2D canvas).
        frameloop="demand" keeps the panel idle when nothing changes.
        alpha=true makes the canvas background transparent.
      */}
      <Canvas
        orthographic
        camera={{ position: [0, 0, 100], zoom: 1 }}
        frameloop="demand"
        gl={{ alpha: true, antialias: false }}
        style={{
          position: 'absolute', inset: 0,
          cursor: isUvBrush ? 'none' : isUvDrawable ? 'crosshair' : 'default',
        }}
        onPointerDown={handleCanvasPointerDown as any}
        onPointerMove={handleCanvasPointerMove as any}
        onPointerUp={handleCanvasPointerUp as any}
        onPointerLeave={handleCanvasPointerLeave as any}
      >
        <InvalidateCapture target={invalidateRef} />
        <UVScene
          baseTexRef={baseTexRef}
          strokeTex={strokeTex}
          checkerTex={checkerTex}
          toolMode={toolMode}
          brushOpacity={brushOpacity}
          bgMode={bgMode}
        />
      </Canvas>

      {/* Clipboard paste overlay */}
      {pasteOverlay && clipboardPasteCanvas && (
        <div
          style={{
            position: 'absolute',
            left:   `${(1 - pasteOverlay.fracW) / 2 * 100}%`,
            top:    `${(1 - pasteOverlay.fracH) / 2 * 100}%`,
            width:  `${pasteOverlay.fracW * 100}%`,
            height: `${pasteOverlay.fracH * 100}%`,
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img
            src={pasteImgSrc}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.8 }}
          />
          <div style={{ position: 'absolute', inset: 0, border: '2px dashed #558cf4', borderRadius: 1 }} />
        </div>
      )}

      {/* Brush cursor ring */}
      <div
        ref={brushCursorRef}
        style={{
          position: 'absolute',
          display: 'none',
          border: '1.5px solid rgba(255,255,255,0.85)',
          borderRadius: '50%',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 9998,
          boxSizing: 'border-box',
        }}
      />

      {/* Blur kernel radius inner ring — same color as outer, half alpha */}
      <div
        ref={blurInnerCursorRef}
        style={{
          position: 'absolute',
          display: 'none',
          border: '1.5px solid rgba(34,211,238,0.425)',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 9997,
          boxSizing: 'border-box',
        }}
      />

      {/* Dup-mode dot row */}
      <div
        ref={dupCursorRef}
        style={{
          display: 'none',
          position: 'absolute',
          flexDirection: 'row',
          gap: 4,
          alignItems: 'center',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 9999,
        }}
      >
        {Array.from({ length: dotCount }).map((_, i) => (
          <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor, boxShadow: '0 0 0 1px rgba(0,0,0,0.55)' }} />
        ))}
      </div>

      {/* Bottom-right: save / copy */}
      <div style={{ position: 'absolute', bottom: 4, right: 4, display: 'flex', gap: 4, zIndex: 20 }}>
        <Tooltip title="Copy current frame to clipboard" placement="left">
          <div style={btnBase} onClick={handleCopyFrame}>{copiedSuccess ? <CheckOutlined /> : <CopyOutlined />}</div>
        </Tooltip>
        <Tooltip title="Save current frame as PNG" placement="left">
          <div style={btnBase} onClick={handleSaveFrame}><FileImageOutlined /></div>
        </Tooltip>
      </div>

      {/* Bottom-left: viewport swap toggle */}
      {!uvViewportMain && (
        <div style={{ position: 'absolute', bottom: 4, left: 4, zIndex: 20 }}>
          <Tooltip title="Switch to UV viewport (draw directly on texture)" placement="right">
            <div style={btnBase} onClick={() => setUvViewportMain(true)}><FullscreenOutlined /></div>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default UVPanel;
